import changePasswordPopupStyles from "./changePasswordPopup.module.css";
import formsStyles from "../../forms/forms.module.css";
import React, { useState } from "react";
import * as Yup from "yup";
import { IPopupWithoutProps } from "../../../types/types";
import { useFormik } from "formik";
import { passwordValidationSchema } from "../../../utils/validation";
import { tWithKeyAttr as t } from "../../../i18nHelper";


function ChangePasswordPopup({
  onClose,
  updatePassword,
  requireCurrentPassword = false,
}: IPopupWithoutProps & {
  updatePassword: (newPassword: string, currentPassword?: string) => void;
  requireCurrentPassword?: boolean;
}) {
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [repeatPasswordVisible, setRepeatPasswordVisible] = useState(false);

  const validationSchema = requireCurrentPassword
    ? passwordValidationSchema.shape({
        currentPassword: Yup.string().required("PASSWORD_REQUIRED"),
      })
    : passwordValidationSchema;

  const formik = useFormik({
    initialValues: { currentPassword: "", password: "", repeatPassword: "" },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      updatePassword(values.password, requireCurrentPassword ? values.currentPassword : undefined);
      onClose();
    },
  });

  const toggleCurrentPasswordVisibility = () => {
    setCurrentPasswordVisible((prev) => !prev);
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  const toggleRepeatPasswordVisibility = () => {
    setRepeatPasswordVisible((prev) => !prev);
  };

  return (
    <article className={changePasswordPopupStyles.popup} data-testid="change-password-popup">
      <h2 className={changePasswordPopupStyles.popup__title}>{t('CHANGE_PASSWORD')}</h2>
      <form
        onSubmit={formik.handleSubmit}
        className={changePasswordPopupStyles.popup__form}
      >
        {requireCurrentPassword && (
          <>
            <label
              htmlFor="currentPassword"
              className={changePasswordPopupStyles.popup__label}
            >{t('CURRENT_PASSWORD')}</label>
            <div className="inputContainer">
              <input
                type={currentPasswordVisible ? "text" : "password"}
                name="currentPassword"
                id="currentPassword"
                data-testid="current-password"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`${formsStyles.form__input} ${
                  formik.touched.currentPassword && formik.errors.currentPassword
                    ? formsStyles.form__input_invalid
                    : ""
                }`}
              />
              <button
                type="button"
                className={`passwordToggle ${currentPasswordVisible && "passwordToggle_visible"}`}
                onClick={toggleCurrentPasswordVisibility}
              />
            </div>
            {formik.touched.currentPassword && formik.errors.currentPassword && (
              <p className={changePasswordPopupStyles.popup__invalid_message}>
                {t(formik.errors.currentPassword)}
              </p>
            )}
          </>
        )}

        <label
          htmlFor="password"
          className={changePasswordPopupStyles.popup__label}
        >{t('PASSWORD')}</label>
        <div className="inputContainer">
          <input
            type={passwordVisible ? "text" : "password"}
            name="password"
            id="password"
            data-testid="set-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`${formsStyles.form__input} ${
              formik.touched.password && formik.errors.password
                ? formsStyles.form__input_invalid
                : ""
            }`}
          />
          <button
            type="button"
            className={`passwordToggle ${
              passwordVisible &&
              "passwordToggle_visible"
            }`}
            onClick={togglePasswordVisibility}
          />
        </div>
        {formik.touched.password && formik.errors.password && (
          <p className={changePasswordPopupStyles.popup__invalid_message} data-testid="set-password-error">
            {t(formik.errors.password)}
          </p>
        )}

        <label
          htmlFor="repeatPassword"
          className={changePasswordPopupStyles.popup__label}
        >{t('ENTER_THE_PASSWORD_AGAIN')}</label>
        <div className="inputContainer">
          <input
            type={repeatPasswordVisible ? "text" : "password"}
            name="repeatPassword"
            id="repeatPassword"
            data-testid="repeat-password"
            value={formik.values.repeatPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`${formsStyles.form__input} ${
              formik.touched.repeatPassword && formik.errors.repeatPassword
                ? formsStyles.form__input_invalid
                : ""
            }`}
          />
          <button
            type="button"
            className={`passwordToggle ${
              repeatPasswordVisible &&
              "passwordToggle_visible"
            }`}
            onClick={toggleRepeatPasswordVisibility}
          />
        </div>
        {formik.touched.repeatPassword && formik.errors.repeatPassword && (
          <p className={changePasswordPopupStyles.popup__invalid_message} data-testid="repeat-password-error">
            {t(formik.errors.repeatPassword)}
          </p>
        )}

        <p className={changePasswordPopupStyles.popup__text}>
          {t("PASSWORd_SETTING_INSTRACTIONS")}
        </p>

        <button
          type="submit"
          disabled={!formik.isValid || !formik.dirty}
          data-testid="save-password"
          className={`${changePasswordPopupStyles.popup__submitBtn} ${
            !formik.isValid || !formik.dirty
              ? changePasswordPopupStyles.popup__submitBtn_disabled
              : ""
          }`}
        >{t('SAVE')}</button>
      </form>
    </article>
  );
}

export default ChangePasswordPopup;
